import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { CookieBanner } from '../components/CookieBanner';
import { useAnalytics } from '../lib/analytics';
import { SiteFooter } from '../components/layout/SiteFooter';
import { SiteHeader } from '../components/layout/SiteHeader';
import { HeroHeaderContext } from './HeroHeader';
import { HideFooterContext } from './HideFooter';
import { ModalsContext, type SiteModal } from './Modals';

/*
 * The two public forms are lazy because they only ever open on a click, and they are expensive:
 * zod + react-hook-form together were ~390 kB of the chunk index.html modulepreloads. No
 * visitor needs a validation library to read a page. No Suspense fallback is rendered — the
 * overlay simply appears once the chunk lands, which is a beat, not a blank screen.
 */
const ContactModal = lazy(() =>
  import('../features/forms/ContactModal').then((m) => ({ default: m.ContactModal })),
);
const MembershipModal = lazy(() =>
  import('../features/forms/MembershipModal').then((m) => ({ default: m.MembershipModal })),
);

/**
 * The public site chrome: every inner page is SiteHeader → content → SiteFooter (§7).
 *
 * The header is FIXED and overlays the page, so it can sit transparently over the Home
 * hero (§25.2). Pages that render a full-bleed hero call useHeroHeader() to flip
 * `heroHeader` on; every other page leaves it off, which keeps the header opaque AND adds
 * top padding so content is not hidden under the fixed bar.
 *
 * The Offers "Coming Soon" template has NO footer (§7.19); that opts out via useHideFooter().
 *
 * The contact + membership modals (§7.20) are mounted HERE, once, because their triggers live
 * in both the header ("Become A Member") and the footer ("Contact Us").
 *
 * Analytics is initialised here for the same reason: one mount covers every public route.
 */
export function SiteLayout() {
  // GA4 page views (§19). Here rather than in a page, so every public route is covered and
  // the admin bundle — which does not render SiteLayout — is not tracked.
  useAnalytics();

  const [heroHeader, setHeroHeader] = useState(false);
  const [hideFooter, setHideFooter] = useState(false);
  const [modal, setModal] = useState<SiteModal>(null);

  const close = useCallback(() => setModal(null), []);
  const modals = useMemo(
    () => ({ open: (which: Exclude<SiteModal, null>) => setModal(which), close }),
    [close],
  );

  return (
    <HeroHeaderContext.Provider value={setHeroHeader}>
      <HideFooterContext.Provider value={setHideFooter}>
        <ModalsContext.Provider value={modals}>
          <div className="flex min-h-screen flex-col bg-canvas">
            <SiteHeader overHero={heroHeader} />
            <main className={`flex-1 ${heroHeader ? '' : 'pt-16'}`}>
              {/*
                One boundary for every lazily-loaded route (see router.tsx). The fallback
                reserves height rather than showing a spinner: a zero-height fallback would
                collapse the page and then push the footer back down when the chunk lands,
                which is exactly the layout shift §17 caps at CLS < 0.1.
              */}
              <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
                <Outlet />
              </Suspense>
            </main>
            {!hideFooter && <SiteFooter />}
          </div>

          <Suspense fallback={null}>
            {modal === 'contact' && <ContactModal onClose={close} />}
            {modal === 'membership' && <MembershipModal onClose={close} />}
          </Suspense>

          {/* Gates GA4 (§19). Not lazy: it must be able to appear on first paint. */}
          <CookieBanner />
        </ModalsContext.Provider>
      </HideFooterContext.Provider>
    </HeroHeaderContext.Provider>
  );
}
